<script setup>
/**
 * One change, applied to every selected transaction. Each field is opt-in:
 * what you do not switch on is left exactly as it was, because the whole point
 * of editing fifty rows at once is not having to restate the forty-nine things
 * that were already right.
 */
import { computed, reactive, ref, shallowRef, watch } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import Picker from '../ui/Picker.vue'
import { fmt, withCategory, withPaymentType } from '../../stores'
import { state, closeSheet, stopSelecting, toast } from '../../stores/state'
import { t } from '../../stores/i18n'
import { fullDay } from '../../lib/dates'
import { applyBulk, deleteTransactions } from '../../stores/bulk'
import { addTag, tagIdsOf } from '../../stores/tags'
import { transactionsByIds } from '../../stores/transactions'

// The selection can reach far past the rows the list has read ("all" takes in
// the whole view), so the rows are asked for by id.
const chosen = shallowRef([])
watch(
  () => [...state.ui.selected],
  async (ids) => {
    const rows = await transactionsByIds(ids)
    if (ids.join() === state.ui.selected.join()) chosen.value = rows
  },
  { immediate: true },
)
const total = computed(() => chosen.value.reduce((sum, t) => sum + t.amount, 0))

const form = reactive({
  setCategory: false,
  category: '',
  setDescription: false,
  description: '',
  setPaymentType: false,
  payment_type: '',
  setNotes: false,
  notes: '',
})

/**
 * One category for all of them. The chip says what the selection already looks
 * like, so "they are all Groceries already" reads differently from "they are a
 * mixture" before you replace either.
 */
const categoryCounts = computed(() => {
  const counts = new Map()
  for (const tx of chosen.value) {
    if (tx.category) counts.set(tx.category, (counts.get(tx.category) ?? 0) + 1)
  }
  return counts
})

const categoryLook = (name) => {
  if (form.setCategory) return form.category === name ? { tone: 'on' } : { tone: 'off' }
  const count = categoryCounts.value.get(name) ?? 0
  if (!count) return { tone: 'off' }
  if (count === chosen.value.length) return { tone: 'on', note: 'on all of them' }
  return {
    tone: 'part',
    note: `on ${count} of ${chosen.value.length}`,
    tail: `${count}/${chosen.value.length}`,
  }
}

/** Picking one is itself the decision to set it; picking it again clears it. */
function setCategory(name) {
  const clearing = form.setCategory && form.category === name
  form.setCategory = true
  form.category = clearing ? '' : name
}
/** Payment types read like categories: one for all of them, or left alone. */
const paymentTypeLook = (name) =>
  form.setPaymentType && form.payment_type === name ? { tone: 'on' } : { tone: 'off' }
function setPaymentType(name) {
  const clearing = form.setPaymentType && form.payment_type === name
  form.setPaymentType = true
  form.payment_type = clearing ? '' : name
}

/** tag id -> 'add' | 'remove'; anything else is left exactly as it is. */
const tagOps = reactive({})

/** How many of the selected rows already wear each tag. */
const tagCounts = computed(() => {
  const counts = new Map(state.tags.map((tag) => [tag.id, 0]))
  for (const tx of chosen.value) {
    for (const id of tagIdsOf(tx)) counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  return counts
})

const tagState = (id) => {
  const have = tagCounts.value.get(id) ?? 0
  return have === 0 ? 'none' : have === chosen.value.length ? 'all' : 'some'
}

/**
 * What a tag says and what a tap does next. A tag they all already wear opens
 * on "remove", because adding it again would do nothing; one only some of them
 * wear offers to give it to the rest first.
 */
const tagLook = (id) => {
  const op = tagOps[id]
  const has = tagState(id)
  const count = tagCounts.value.get(id) ?? 0
  const part = `${count}/${chosen.value.length}`
  if (op === 'add') return { tone: 'on', note: 'will go on all of them' }
  if (op === 'remove') return { tone: 'gone', note: 'will come off all of them' }
  if (has === 'all') return { tone: 'on', note: 'on all of them' }
  if (has === 'some') return { tone: 'part', note: `on ${count} of ${chosen.value.length}`, tail: part }
  return { tone: 'off' }
}
const busy = ref('')
const error = ref('')
const confirmDelete = ref(false)

function cycleTag(id) {
  const op = tagOps[id]
  const has = tagState(id)
  if (has === 'all') tagOps[id] = op === 'remove' ? undefined : 'remove'
  else if (has === 'some') tagOps[id] = op === undefined ? 'add' : op === 'add' ? 'remove' : undefined
  else tagOps[id] = op === 'add' ? undefined : 'add'
}

/** A tag that does not exist yet is made here and put straight on the pile. */
async function addNamedTag(name) {
  const tag = await addTag(name)
  if (tag) tagOps[tag.id] = 'add'
}

const changes = computed(() => {
  const out = {}
  if (form.setCategory) out.category = form.category
  if (form.setDescription) out.description = form.description
  if (form.setPaymentType) out.payment_type = form.payment_type
  if (form.setNotes) out.notes = form.notes
  const addTags = Object.keys(tagOps).filter((id) => tagOps[id] === 'add')
  const removeTags = Object.keys(tagOps).filter((id) => tagOps[id] === 'remove')
  if (addTags.length) out.addTags = addTags
  if (removeTags.length) out.removeTags = removeTags
  return out
})

const nothingToDo = computed(() => !Object.keys(changes.value).length)

async function apply() {
  error.value = ''
  busy.value = 'apply'
  const { updated } = await applyBulk(state.ui.selected, changes.value)
  busy.value = ''
  toast(t('bulk.updated', { count: updated }), 'good')
  stopSelecting()
  closeSheet()
}

async function destroy() {
  if (!confirmDelete.value) {
    confirmDelete.value = true
    return
  }
  busy.value = 'delete'
  const count = await deleteTransactions(state.ui.selected)
  busy.value = ''
  toast(t('bulk.deleted', { count }))
  stopSelecting()
  closeSheet()
}
</script>

<template>
  <Sheet
    :title="t('bulk.title', { count: chosen.length })"
    :subtitle="t('bulk.subtitle', { amount: fmt(total, { signed: true }) })"
    @close="closeSheet"
  >
    <div class="grid gap-4">
      <div>
        <label class="flex items-center gap-2 text-[13px]">
          <input v-model="form.setCategory" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
          {{ t('bulk.setCategory') }}
        </label>
        <Picker
          id="bulk-cat"
          class="mt-2"
          single
          :create-label="t('common.use')"
          :placeholder="t('common.searchCategories')"
          :value="form.setCategory ? form.category : ''"
          :options="withCategory(form.category)"
          :look="categoryLook"
          @pick="setCategory"
          @create="setCategory"
        />
        <p v-if="form.setCategory && !form.category" class="mt-1.5 text-[12px] text-faint">
          {{ t('bulk.noCategory') }}
        </p>
      </div>

      <div>
        <label class="flex items-center gap-2 text-[13px]">
          <input v-model="form.setDescription" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
          {{ t('bulk.setDescription') }}
        </label>
        <input
          v-if="form.setDescription"
          v-model="form.description"
          class="input mt-2"
          :placeholder="t('bulk.descriptionPlaceholder')"
        />
      </div>

      <div>
        <label class="flex items-center gap-2 text-[13px]">
          <input v-model="form.setPaymentType" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
          {{ t('bulk.setPaymentType') }}
        </label>
        <div v-if="form.setPaymentType" class="mt-2">
          <Picker
            id="bulk-payment"
            single
            :create-label="t('common.use')"
            :placeholder="t('txSheet.paymentTypePlaceholder')"
            :value="form.payment_type"
            :options="withPaymentType(form.payment_type)"
            :look="paymentTypeLook"
            @pick="setPaymentType"
            @create="setPaymentType"
          />
        </div>
      </div>

      <div>
        <label class="flex items-center gap-2 text-[13px]">
          <input v-model="form.setNotes" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
          {{ t('bulk.setNotes') }}
        </label>
        <textarea
          v-if="form.setNotes"
          v-model="form.notes"
          class="input mt-2 min-h-[60px]"
          rows="2"
        />
      </div>

      <div>
        <label class="label" for="bulk-tag">{{ t('charts.tags') }}</label>
        <Picker
          id="bulk-tag"
          :options="state.tags"
          :look="tagLook"
          @pick="cycleTag"
          @create="addNamedTag"
          :placeholder="t('common.searchTags')"
        />
        <p class="mt-1.5 text-[12px] text-faint">{{ t('bulk.tagsHint') }}</p>
      </div>

      <p v-if="error" class="flex items-center gap-2 text-[13px] text-danger">
        <Icon name="alert" :size="15" /> {{ error }}
      </p>
    </div>

    <template #footer>
      <button class="btn-danger" :disabled="busy !== ''" @click="destroy">
        <Icon name="trash" :size="16" />
        {{ confirmDelete ? t('common.confirmDelete') : t('common.delete') }}
      </button>
      <div class="flex-1" />
      <button class="btn-ghost" @click="closeSheet">{{ t('common.cancel') }}</button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" :disabled="nothingToDo || busy !== ''" @click="apply">
        {{ busy === 'apply' ? t('bulk.applying') : t('common.apply') }}
        <Kbd combo="mod+enter" />
      </button>
    </template>
  </Sheet>
</template>
